import { useState, useCallback, useEffect, useMemo } from 'react';
import { useCustomerService } from 'services/customers';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { Customer } from 'types/customer';

const CUSTOMERS_CACHE_KEY_BASE = 'customers_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const getCustomersCacheKey = (
  includeVoided: boolean,
  companyId: string | null | undefined,
) => {
  const baseKey = `${CUSTOMERS_CACHE_KEY_BASE}_${includeVoided ? 'withVoided' : 'withoutVoided'}`;
  return getCacheKeyWithCompany(baseKey, companyId);
};

const sortCustomersByDateDesc = (customers: Customer[]) =>
  [...customers].sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return bTime - aTime;
  });

interface UseCustomersDataOptions {
  includeVoided: boolean;
}

interface UseCustomersDataReturn {
  customers: Customer[];
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => void;
  addCustomer: (customer: Customer) => void;
  updateCustomer: (customer: Customer) => void;
  removeCustomer: (id: string) => void;
}

export function useCustomersData(
  options: UseCustomersDataOptions,
): UseCustomersDataReturn {
  const { includeVoided } = options;
  const customerService = useCustomerService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create cache key with company ID suffix
  const customersCacheKey = useMemo(
    () => getCustomersCacheKey(includeVoided, activeCompanyId),
    [includeVoided, activeCompanyId],
  );

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedCustomers,
    setData: setCachedCustomers,
    isInitialized: cacheInitialized,
  } = useLocalStorage<Customer[]>({
    key: customersCacheKey,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  // Reset cache check when includeVoided or company changes (cache key changes)
  useEffect(() => {
    setCacheChecked(false);
    setLoading(true);
    // Clear customers temporarily while loading new cache
    setCustomers([]);
    // Reset error state
    setError(null);
  }, [includeVoided, activeCompanyId]);

  // Load cached data on mount or when company/cache key changes
  useEffect(() => {
    if (!cacheInitialized || !isCompanyInitialized) return; // Wait for both to be ready

    if (cachedCustomers) {
      // Use cached data - it's still valid (hook already checked expiration)
      setCustomers(sortCustomersByDateDesc(cachedCustomers));
      setLoading(false);
      setCacheChecked(true);
    } else {
      // No cache or expired - mark as checked so we can fetch
      setCacheChecked(true);
    }
  }, [
    cachedCustomers,
    cacheInitialized,
    isCompanyInitialized,
    customersCacheKey,
  ]);

  const fetchCustomers = useCallback(
    async (isReload = false) => {
      // Don't fetch if company is not initialized yet
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      // Fetch fresh data (either cache expired, doesn't exist, or isReload is true)
      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const fetchedCustomers =
          await customerService.getAllCustomers(includeVoided);
        const sortedCustomers = sortCustomersByDateDesc(fetchedCustomers);
        setCustomers(sortedCustomers);

        // Limit cached customers to first 50 to avoid large data issues
        const customersToCache =
          sortedCustomers.length > 50
            ? sortedCustomers.slice(0, 50)
            : sortedCustomers;

        // Save to cache with encryption
        await setCachedCustomers(customersToCache);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load customers. Please check your connection.';
        setError(errorMessage);
      } finally {
        if (isReload) {
          setReloading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      includeVoided,
      customerService,
      setCachedCustomers,
      isCompanyInitialized,
      activeCompanyId,
    ],
  );

  // Only fetch data if cache is checked, company is initialized, and no cached data exists
  useEffect(() => {
    if (
      cacheChecked &&
      cacheInitialized &&
      isCompanyInitialized &&
      !cachedCustomers
    ) {
      void fetchCustomers();
    }
  }, [
    cacheChecked,
    cacheInitialized,
    isCompanyInitialized,
    cachedCustomers,
    fetchCustomers,
    includeVoided,
  ]);

  const reload = useCallback(() => {
    void fetchCustomers(true);
  }, [fetchCustomers]);

  const addCustomer = useCallback(
    (newCustomer: Customer) => {
      setCustomers((prevCustomers) => {
        const nextCustomers = [newCustomer, ...prevCustomers];
        const sortedCustomers = sortCustomersByDateDesc(nextCustomers);

        // Update cache with encrypted data (fire and forget)
        void setCachedCustomers(sortedCustomers);

        return sortedCustomers;
      });
    },
    [setCachedCustomers],
  );

  const updateCustomer = useCallback(
    (updatedCustomer: Customer) => {
      setCustomers((prevCustomers) => {
        const nextCustomers = prevCustomers.map((customer) =>
          customer.id === updatedCustomer.id ? updatedCustomer : customer,
        );

        const sortedCustomers = sortCustomersByDateDesc(nextCustomers);

        // Update cache with encrypted data (fire and forget)
        void setCachedCustomers(sortedCustomers);

        return sortedCustomers;
      });
    },
    [setCachedCustomers],
  );

  const removeCustomer = useCallback(
    (id: string) => {
      setCustomers((prevCustomers) => {
        const nextCustomers = prevCustomers.filter(
          (customer) => customer.id !== id,
        );

        // Update cache with encrypted data (fire and forget)
        void setCachedCustomers(nextCustomers);

        return nextCustomers;
      });
    },
    [setCachedCustomers],
  );

  return {
    customers,
    loading,
    reloading,
    error,
    reload,
    addCustomer,
    updateCustomer,
    removeCustomer,
  };
}
